/* eslint-disable prefer-rest-params */ import { ArraySet } from "./ArraySet";
import { HistoryBuffer } from "./HistoryBuffer";
import { maybeCaptureParent, startCapturingParents, stopCapturingParents } from "./capture";
import { GLOBAL_START_EPOCH } from "./constants";
import { EMPTY_ARRAY, equals, haveParentsChanged } from "./helpers";
import { globalEpoch } from "./transactions";
import { RESET_VALUE } from "./types";
import { logComputedGetterWarning, logDotValueWarning } from "./warnings";
const UNINITIALIZED = Symbol("UNINITIALIZED");
/**
 * Call this inside a computed signal function to determine whether it is the first time the function is being called.
 *
 * Mainly useful for incremental signal computation.
 *
 * @example
 * ```ts
 * const count = atom('count', 0)
 * const double = computed('double', (prevValue) => {
 *   if (isUninitialized(prevValue)) {
 *     print('First time!')
 *   }
 *   return count.get() * 2
 * })
 * ```
 *
 * @param value - The value to check.
 * @public
 */ export const isUninitialized = (value)=>{
    return value === UNINITIALIZED;
};
class WithDiff {
    value;
    diff;
    constructor(value, diff){
        this.value = value;
        this.diff = diff;
    }
}
/**
 * When writing incrementally-computed signals it is convenient (and usually more performant) to incrementally compute the diff too.
 *
 * You can use this function to wrap the return value of a computed signal function to indicate that the diff should be used instead of calculating a new one with [[AtomOptions.computeDiff]].
 *
 * @example
 * ```ts
 * const count = atom('count', 0)
 * const double = computed('double', (prevValue) => {
 *   const nextValue = count.get() * 2
 *   if (isUninitialized(prevValue)) {
 *     return nextValue
 *   }
 *   return withDiff(nextValue, nextValue - prevValue)
 * }, { historyLength: 10 })
 * ```
 *
 *
 * @param value - The value.
 * @param diff - The diff.
 * @public
 */ export function withDiff(value, diff) {
    return new WithDiff(value, diff);
}
/**
 * @internal
 */ export class _Computed {
    name;
    derive;
    lastChangedEpoch;
    lastTraversedEpoch;
    /**
   * The epoch when the reactor was last checked.
   */ lastCheckedEpoch;
    parents;
    parentEpochs;
    children;
    // eslint-disable-next-line no-restricted-syntax
    get isActivelyListening() {
        return !this.children.isEmpty;
    }
    historyBuffer;
    // The last-computed value of this signal.
    state;
    computeDiff;
    isEqual;
    constructor(/**
     * The name of the signal. This is used for debugging and performance profiling purposes. It does not need to be globally unique.
     */ name, /**
     * The function that computes the value of the signal.
     */ derive, options){
        this.name = name;
        this.derive = derive;
        this.lastChangedEpoch = GLOBAL_START_EPOCH;
        this.lastTraversedEpoch = GLOBAL_START_EPOCH;
        this.lastCheckedEpoch = GLOBAL_START_EPOCH;
        this.parents = [];
        this.parentEpochs = [];
        this.children = new ArraySet();
        this.state = UNINITIALIZED;
        if (options?.historyLength) {
            this.historyBuffer = new HistoryBuffer(options.historyLength);
        }
        this.computeDiff = options?.computeDiff;
        this.isEqual = options?.isEqual ?? equals;
    }
    __unsafe__getWithoutCapture() {
        const isNew = this.lastChangedEpoch === GLOBAL_START_EPOCH;
        if (!isNew && (this.lastCheckedEpoch === globalEpoch || !haveParentsChanged(this))) {
            this.lastCheckedEpoch = globalEpoch;
            return this.state;
        }
        try {
            startCapturingParents(this);
            const result = this.derive(this.state, this.lastCheckedEpoch);
            const newState = result instanceof WithDiff ? result.value : result;
            if (this.state === UNINITIALIZED || !this.isEqual(newState, this.state)) {
                if (this.historyBuffer && !isNew) {
                    const diff = result instanceof WithDiff ? result.diff : undefined;
                    this.historyBuffer.pushEntry(this.lastChangedEpoch, globalEpoch, diff ?? this.computeDiff?.(this.state, newState, this.lastCheckedEpoch, globalEpoch) ?? RESET_VALUE);
                }
                this.lastChangedEpoch = globalEpoch;
                this.state = newState;
            }
            this.lastCheckedEpoch = globalEpoch;
            return this.state;
        } finally{
            stopCapturingParents();
        }
    }
    get() {
        const value = this.__unsafe__getWithoutCapture();
        maybeCaptureParent(this);
        return value;
    }
    /**
   * @deprecated Use [[get]] instead.
   */ // eslint-disable-next-line no-restricted-syntax
    get value() {
        logDotValueWarning();
        return this.get();
    }
    getDiffSince(epoch) {
        // need to call .get() to ensure both that this derivation is up to date
        // and that tracking happens correctly
        this.get();
        if (epoch >= this.lastChangedEpoch) {
            return EMPTY_ARRAY;
        }
        return this.historyBuffer?.getChangesSince(epoch) ?? RESET_VALUE;
    }
}
function computedMethodAnnotation(options = {}, _target, key, descriptor) {
    const originalMethod = descriptor.value;
    const derivationKey = Symbol.for("__@supersig/state__computed__" + key);
    descriptor.value = function() {
        let d = this[derivationKey];
        if (!d) {
            d = new _Computed(key, originalMethod.bind(this), options);
            Object.defineProperty(this, derivationKey, {
                enumerable: false,
                configurable: false,
                writable: false,
                value: d
            });
        }
        return d.get();
    };
    descriptor.value[isComputedMethodKey] = true;
    return descriptor;
}
function computedAnnotation(options = {}, _target, key, descriptor) {
    if (descriptor.get) {
        logComputedGetterWarning();
        return computedGetterAnnotation(options, _target, key, descriptor);
    } else {
        return computedMethodAnnotation(options, _target, key, descriptor);
    }
}
function computedGetterAnnotation(options = {}, _target, key, descriptor) {
    const originalMethod = descriptor.get;
    const derivationKey = Symbol.for("__@supersig/state__computed__" + key);
    descriptor.get = function() {
        let d = this[derivationKey];
        if (!d) {
            d = new _Computed(key, originalMethod.bind(this), options);
            Object.defineProperty(this, derivationKey, {
                enumerable: false,
                configurable: false,
                writable: false,
                value: d
            });
        }
        return d.get();
    };
    return descriptor;
}
const isComputedMethodKey = "@@__isComputedMethod__@@";
/**
 * Retrieves the underlying computed instance for a given property created with the [[computed]]
 * decorator.
 *
 * @example
 * ```ts
 * class Counter {
 *   max = 100
 *   count = atom(0)
 *
 *   @computed getRemaining() {
 *     return this.max - this.count.get()
 *   }
 * }
 *
 * const c = new Counter()
 * const remaining = getComputedInstance(c, 'getRemaining')
 * remaining.get() === 100 // true
 * c.count.set(13)
 * remaining.get() === 87 // true
 * ```
 *
 * @param obj - The object
 * @param propertyName - The property name
 * @public
 */ export function getComputedInstance(obj, propertyName) {
    const key = Symbol.for("__@supersig/state__computed__" + propertyName.toString());
    let inst = obj[key];
    if (!inst) {
        // deref to make sure it exists first
        const val = obj[propertyName];
        if (typeof val === "function" && val[isComputedMethodKey]) {
            val.call(obj);
        }
        inst = obj[key];
    }
    return inst;
}
/** @public */ export function computed() {
    if (arguments.length === 1) {
        const options = arguments[0];
        return (target, key, descriptor)=>computedAnnotation(options, target, key, descriptor);
    } else if (typeof arguments[0] === "string") {
        return new _Computed(arguments[0], arguments[1], arguments[2]);
    } else {
        return computedAnnotation(undefined, arguments[0], arguments[1], arguments[2]);
    }
}
/**
 * Returns true if the given value is a computed signal.
 *
 * @param value
 * @returns {value is Computed<any>}
 * @public
 */ export function isComputed(value) {
    return value && value instanceof _Computed;
}
